import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import QRCode from 'qrcode';

import { Copy } from './copy';

interface ExpoQrPreviewProps {
	content?: string;
	instructions?: string;
	fallbackUrl?: string;
	className?: string;
}

export function ExpoQrPreview({ content, instructions, fallbackUrl, className }: ExpoQrPreviewProps) {
	const [qrDataUrl, setQrDataUrl] = useState<string>();
	const [error, setError] = useState<string>();

	useEffect(() => {
		let isMounted = true;

		if (!content) {
			setQrDataUrl(undefined);
			setError(undefined);
			return () => {
				isMounted = false;
			};
		}

		setError(undefined);

		const generate = async () => {
			try {
				const dataUrl = await QRCode.toDataURL(content, {
					errorCorrectionLevel: 'M',
					margin: 1,
					scale: 8,
					color: {
						dark: '#0f172a',
						light: '#ffffff',
					},
				});

				if (!isMounted) return;
				setQrDataUrl(dataUrl);
			} catch (err: unknown) {
				console.error('Failed to generate Expo QR code', err);
				if (!isMounted) return;
				setError('We couldn\'t generate the QR code automatically. Use the manual URL below.');
				setQrDataUrl(undefined);
			}
		};

		void generate();

		return () => {
			isMounted = false;
		};
	}, [content]);

	const instructionItems = useMemo(() => {
		if (!instructions) return [] as string[];
		return instructions
			.split(/(?<=\.)\s+/)
			.map(item => item.trim())
			.filter(Boolean);
	}, [instructions]);

	const manualUrl = content || fallbackUrl;

	return (
		<div
			className={clsx(
				'flex h-full w-full flex-col items-center justify-center gap-8 bg-bg-3 p-6 text-center',
				className,
			)}
		>
			<div className="flex flex-col items-center gap-4">
				{qrDataUrl ? (
					<img
						src={qrDataUrl}
						alt="Expo Go QR code"
						className="h-56 w-56 rounded-xl border border-border-primary bg-white p-4 drop-shadow"
					/>
				) : (
					<div className="flex h-56 w-56 items-center justify-center rounded-xl border border-dashed border-border-primary/50 bg-bg-2 text-sm text-text-primary/60">
						{error ?? 'Generating QR code...'}
					</div>
				)}
				{manualUrl && (
					<div className="flex flex-wrap items-center justify-center gap-2 rounded-lg bg-bg-2 px-3 py-2 text-left shadow-sm">
						<span className="font-mono text-xs text-text-primary/70 break-words max-w-xs">{manualUrl}</span>
						<Copy text={manualUrl} />
					</div>
				)}
			</div>

			<div className="flex max-w-2xl flex-col items-center gap-3 text-sm text-text-primary/70">
				<span className="text-base font-medium text-text-primary">Open in Expo Go</span>
				{instructionItems.length > 0 ? (
					<ol className="flex list-decimal flex-col gap-1 text-left">
						{instructionItems.map((item, index) => (
							<li key={index} className="pl-2 text-sm text-text-primary/70">
								{item}
							</li>
						))}
					</ol>
				) : (
					<p>
						Scan the QR code with the <span className="font-medium text-text-primary">Expo Go</span> app to open your project on a physical device or emulator.
					</p>
				)}
				{error && !qrDataUrl && manualUrl && (
					<p className="text-xs text-accent/80">
						If scanning fails, copy the URL above and paste it manually into Expo Go.
					</p>
				)}
			</div>
		</div>
	);
}
